import { useNavigate } from "react-router-dom";
import { User, HelpCircle, CreditCard, LogOut, MessageCircle, Wallet, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useTotalUnreadCount } from "@/hooks/useUnreadCounts";
import { UnreadBadge } from "@/components/messaging/UnreadBadge";
import { cn } from "@/lib/utils";
import { isStandaloneDisplay, getInstallGuidance, canNativeInstall, promptNativeInstall, copyAppUrl } from "@/lib/pwaInstall";
import { toast } from "@/hooks/use-toast";

function getInitial(profile: { first_name?: string | null; full_name?: string | null } | null, email?: string | null): string {
  if (profile?.first_name?.trim()) return profile.first_name.trim().charAt(0).toUpperCase();
  if (profile?.full_name?.trim()) return profile.full_name!.trim().charAt(0).toUpperCase();
  if (email) return email.charAt(0).toUpperCase();
  return "?";
}

export function ProfileDropdown({ variant = "default" }: { variant?: "default" | "shell" } = {}) {
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();
  const { data: totalUnread = 0 } = useTotalUnreadCount(!!user);

  const getInboxPath = () => {
    if (profile?.role === "owner") return "/owner/inbox";
    return "/cleaner/inbox";
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "relative rounded-full overflow-hidden",
            variant === "shell" ? "h-[42px] w-[42px] bg-[#e2eeed] hover:bg-[#d3e6e4]" : "h-9 w-9",
          )}
          aria-label="Account menu"
        >
          {profile?.avatar_url?.trim() ? (
            <img
              src={profile.avatar_url.trim()}
              alt=""
              className={cn("rounded-full object-cover", variant === "shell" ? "h-[42px] w-[42px]" : "h-9 w-9")}
              referrerPolicy="no-referrer"
            />
          ) : (
            <span
              className={cn(
                "flex items-center justify-center rounded-full font-semibold text-sm",
                variant === "shell"
                  ? "h-[42px] w-[42px] bg-[#e2eeed] text-[15px] font-bold text-[#15776d]"
                  : "h-9 w-9 bg-primary/10 text-primary"
              )}
            >
              {getInitial(profile, user?.email)}
            </span>
          )}
          {totalUnread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold flex items-center justify-center px-1"
              aria-label={`${totalUnread} unread messages`}
            >
              {totalUnread > 99 ? "99+" : totalUnread > 9 ? "9+" : totalUnread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Account</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => navigate(getInboxPath())}>
          <MessageCircle className="mr-2 h-4 w-4" />
          Inbox
          {totalUnread > 0 && (
            <UnreadBadge count={totalUnread} className="ml-auto" />
          )}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/account")}>
          <User className="mr-2 h-4 w-4" />
          My account
        </DropdownMenuItem>
        {!isStandaloneDisplay() ? (
          <DropdownMenuItem
            onClick={async () => {
              if (canNativeInstall()) {
                await promptNativeInstall();
                return;
              }
              const g = getInstallGuidance();
              if (g.kind === "ios-open-safari") {
                await copyAppUrl();
                toast({
                  title: "Open in Safari first",
                  description: "iPhone won’t show Add to Home Screen in Chrome. Open Safari → paste portal.stravo.ai → Share → Add to Home Screen.",
                });
                return;
              }
              if (g.kind === "ios-safari") {
                toast({
                  title: "Add to Home Screen",
                  description: "Tap Share at the bottom, scroll down, then Add to Home Screen.",
                });
                return;
              }
              navigate("/account");
              toast({
                title: "Install Stravo",
                description: "On iPhone use Safari → Share → Add to Home Screen.",
              });
            }}
          >
            <Smartphone className="mr-2 h-4 w-4" />
            Install app
          </DropdownMenuItem>
        ) : null}
        {profile?.role === "cleaner" && (
          <DropdownMenuItem onClick={() => navigate("/account/payouts")}>
            <Wallet className="mr-2 h-4 w-4" />
            Payouts
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild>
          <a href="mailto:support@stravo.ai" className="cursor-pointer">
            <HelpCircle className="mr-2 h-4 w-4" />
            Support
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem disabled>
          <CreditCard className="mr-2 h-4 w-4" />
          Billing
          <span className="ml-auto text-xs text-muted-foreground">Coming soon</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut} className="text-muted-foreground">
          <LogOut className="mr-2 h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
